'use client';
import type { ElementType } from 'react';
import { Button, DrawerHeader, DrawerTitle } from '@skin-seoul/design-system';
import { useRegionCurrency } from '@skin-seoul/service';
import { useCartDrawer } from '../../../context/CartDrawerContext';
import QueryBoundary from '../../../utils/react/QueryBoundary';
import { CartBenefitsBanner } from '../components/CartBenefitsBanner';
import { CartBenefitsBannerSkeleton } from '../components/CartBenefitsBanner.Skeleton';
import { CartSkinAnalysisRoutine } from '../components/CartSkinAnalysisRoutine';
import { CartRecentlyViewed, CartWishlist } from '../components/CartSavedProducts';
import { useCartAnalytics } from '../components/CartAnalytics';

interface CartEmptyViewProps {
  onClickContinueShopping: () => void;
  linkComponent?: ElementType;
}

export const CartEmptyView = ({ onClickContinueShopping, linkComponent }: CartEmptyViewProps) => {
  const { isOpen, onCartClose } = useCartDrawer();
  const { currency } = useRegionCurrency();
  const { track } = useCartAnalytics();
  return (
    <>
      <DrawerHeader className="items-center bg-generic-0 flex h-12 shrink-0 flex-row justify-between p-3 w-full">
        <DrawerTitle className="font-medium text-center text-label-large w-full">My Bag</DrawerTitle>
      </DrawerHeader>
      <div className="min-h-0 w-full flex-1 overflow-y-auto scrollbar-none pb-6" data-vaul-no-drag>
        <div className="mt-2 px-4">
          <QueryBoundary clientOnly loadingFallback={<CartBenefitsBannerSkeleton currency={currency} empty />}>
            <CartBenefitsBanner isTrackingActive={isOpen} layout="empty" />
          </QueryBoundary>
        </div>
        <div className="flex h-[248px] flex-col items-center justify-center gap-3">
          <p className="text-label-large font-medium text-neutral-500">Your cart is empty.</p>
          <Button
            variant="primary"
            className="h-9 w-[148px] rounded-lg bg-primary-500 px-3 text-paragraph-small font-medium"
            onClick={() => {
              track('continue_shopping_clicked');
              onClickContinueShopping();
            }}
          >
            Continue shopping
          </Button>
        </div>
        <CartSkinAnalysisRoutine currency={currency} linkComponent={linkComponent} onProductClick={onCartClose} />
        <CartWishlist currency={currency} linkComponent={linkComponent} onProductClick={onCartClose} />
        <CartRecentlyViewed currency={currency} linkComponent={linkComponent} onProductClick={onCartClose} />
      </div>
    </>
  );
};
